import React, { useState, useEffect } from 'react';
import { X, Shield, Users, CreditCard, Receipt } from 'lucide-react';
import API_BASE_URL from '../../config';

const TrainerPermissionsModal = ({ isOpen, onClose, trainer, gymCode }) => {
  const [memberTabEnabled, setMemberTabEnabled] = useState(false);
  const [paymentTabEnabled, setPaymentTabEnabled] = useState(false);
  const [expensesSubmitEnabled, setExpensesSubmitEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    if (isOpen && trainer && gymCode) {
      const fetchPermissions = async () => {
        setLoading(true);
        setError('');
        try {
          const res = await fetch(`${API_BASE_URL}/api/gym/${gymCode}/trainers/${trainer.employeeId}/permissions`, {
            headers: {
              'Authorization': `Bearer ${JSON.parse(localStorage.getItem('accessToken') || '{}').token || ''}`
            }
          });
          if (!res.ok) throw new Error('Failed to load permissions');
          const data = await res.json();
          setMemberTabEnabled(data.memberTabEnabled);
          setPaymentTabEnabled(data.paymentTabEnabled);
          setExpensesSubmitEnabled(data.expensesSubmitEnabled);
        } catch (err) {
          console.error(err);
          setError('Failed to fetch trainer permissions');
        } finally {
          setLoading(false);
        }
      };
      fetchPermissions();
    }
  }, [isOpen, trainer, gymCode]);

  const handleSave = async () => {
    setLoading(true);
    setError('');
    setSaveSuccess(false);
    try {
      const res = await fetch(`${API_BASE_URL}/api/gym/${gymCode}/trainers/${trainer.employeeId}/permissions`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${JSON.parse(localStorage.getItem('accessToken') || '{}').token || ''}`
        },
        body: JSON.stringify({
          memberTabEnabled,
          paymentTabEnabled,
          expensesSubmitEnabled
        })
      });
      if (!res.ok) throw new Error('Failed to update permissions');
      setSaveSuccess(true);
      setTimeout(() => {
        setSaveSuccess(false);
        onClose();
      }, 1000);
    } catch (err) {
      console.error(err);
      setError('Failed to update permissions');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal Content */}
      <div className="relative z-10 w-full max-w-md rounded-2xl bg-[#181818] border border-[#2b2b2b] p-6 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-lime-400" />
            <h3 className="text-lg font-bold text-white">Trainer Permissions</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#2b2b2b] rounded-lg transition-all">
            <X className="w-5 h-5 text-gray-400 hover:text-white" />
          </button>
        </div>

        {/* Trainer Info */}
        <div className="bg-[#222]/50 p-4 rounded-xl border border-[#2b2b2b] mb-6">
          <p className="text-xs text-gray-400">Configuring access for</p>
          <h4 className="text-base font-semibold text-white mt-0.5">{trainer?.fullName}</h4>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-3 rounded-lg text-sm mb-4">
            {error}
          </div>
        )}

        {/* Toggles */}
        <div className="space-y-4 mb-8">
          
          {/* Member Management Toggle */}
          <div className="flex items-center justify-between p-3.5 bg-[#222]/30 border border-[#2b2b2b] rounded-xl hover:border-lime-500/20 transition-all">
            <div className="flex items-start gap-3">
              <Users className="w-5 h-5 text-lime-400 mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-semibold text-white text-sm">Member Management</h5>
                <p className="text-xs text-gray-400 mt-0.5">Allows viewing, adding, extending, and deleting gym members.</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer select-none ml-4 flex-shrink-0">
              <input
                type="checkbox"
                checked={memberTabEnabled}
                onChange={(e) => setMemberTabEnabled(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-[#2b2b2b] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-gray-400 after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-lime-400 peer-checked:after:bg-[#181f10] peer-checked:after:border-transparent"></div>
            </label>
          </div>

          {/* Payment Access Toggle */}
          <div className="flex items-center justify-between p-3.5 bg-[#222]/30 border border-[#2b2b2b] rounded-xl hover:border-lime-500/20 transition-all">
            <div className="flex items-start gap-3">
              <CreditCard className="w-5 h-5 text-lime-400 mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-semibold text-white text-sm">Payments Access</h5>
                <p className="text-xs text-gray-400 mt-0.5">Allows recording payments, viewing history, and deleting transactions.</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer select-none ml-4 flex-shrink-0">
              <input
                type="checkbox"
                checked={paymentTabEnabled}
                onChange={(e) => setPaymentTabEnabled(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-[#2b2b2b] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-gray-400 after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-lime-400 peer-checked:after:bg-[#181f10] peer-checked:after:border-transparent"></div>
            </label>
          </div>

          {/* Submit Expenses Toggle */}
          <div className="flex items-center justify-between p-3.5 bg-[#222]/30 border border-[#2b2b2b] rounded-xl hover:border-lime-500/20 transition-all">
            <div className="flex items-start gap-3">
              <Receipt className="w-5 h-5 text-lime-400 mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-semibold text-white text-sm">Submit Expenses</h5>
                <p className="text-xs text-gray-400 mt-0.5">Allows submitting gym outgoings (salary, maintenance, utilities) for approval.</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer select-none ml-4 flex-shrink-0">
              <input
                type="checkbox"
                checked={expensesSubmitEnabled}
                onChange={(e) => setExpensesSubmitEnabled(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-[#2b2b2b] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-gray-400 after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-lime-400 peer-checked:after:bg-[#181f10] peer-checked:after:border-transparent"></div>
            </label>
          </div>

        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-5 py-2.5 text-sm rounded-lg bg-[#222] hover:bg-[#2b2b2b] text-gray-300 transition-all disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className={`px-5 py-2.5 text-sm font-semibold rounded-lg text-white transition-all shadow-lg flex items-center justify-center min-w-[100px] ${
              saveSuccess 
                ? 'bg-emerald-500 hover:bg-emerald-600' 
                : 'bg-lime-400 hover:bg-lime-300 text-[#181f10] disabled:opacity-50'
            }`}
          >
            {loading ? 'Saving...' : saveSuccess ? 'Saved!' : 'Save Changes'}
          </button>
        </div>

      </div>
    </div>
  );
};

export default TrainerPermissionsModal;
